if (!customElements.get('ob-doria-configurator')) {
  customElements.define('ob-doria-configurator', class extends HTMLElement {
    connectedCallback() {
      if (this.controller || this.initializationObserver) return;
      this.initializationObserver = new MutationObserver(() => this.initialize());
      this.initializationObserver.observe(this.closest('product-info') || this, { childList: true, subtree: true });
      this.initialize();
    }

    initialize() {
      if (!this.isConnected || this.controller) return;
      const variants = this.querySelector('[data-doria-variants]');
      const texts = this.querySelector('[data-doria-texts]');
      if (!variants?.textContent.trim() || !texts?.textContent.trim() || !this.form || !this.selectedColumn) return;
      try {
        this.variants = new Map(JSON.parse(variants.textContent).map(variant => [String(variant.id), variant]));
        this.texts = JSON.parse(texts.textContent);
        this.mediaCounts = new Map(JSON.parse(this.querySelector('[data-doria-media]')?.textContent || '[]').map(media => [String(media.id), Number(media.basinCount)]));
      } catch {
        return;
      }
      this.initializationObserver?.disconnect();
      this.initializationObserver = null;
      this.controller = new AbortController();
      this.busy = false;
      this.money = new Intl.NumberFormat(this.dataset.locale || 'es-ES', { style: 'currency', currency: this.dataset.currency || 'EUR' });
      const signal = this.controller.signal;
      document.addEventListener('submit', event => this.submit(event), { capture: true, signal });
      document.addEventListener('change', event => this.changed(event), { signal });
      document.addEventListener('input', event => this.changed(event), { signal });
      this.galleryObserver = new MutationObserver(() => this.queueGalleryUpdate());
      this.closest('product-info')?.querySelectorAll('media-gallery, product-modal').forEach(gallery => {
        this.galleryObserver.observe(gallery, { childList: true, subtree: true });
      });
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.initializationObserver?.disconnect();
      this.initializationObserver = null;
      this.galleryObserver?.disconnect();
      this.galleryObserver = null;
      if (this.galleryFrame != null) cancelAnimationFrame(this.galleryFrame);
      this.galleryFrame = null;
    }

    get form() {
      const productInfo = this.closest('product-info');
      if (!productInfo) return null;
      return productInfo.querySelector('product-form form') ||
        Array.from(productInfo.querySelectorAll('form')).find(form => form.id === this.dataset.formId) || null;
    }

    get selectedColumn() {
      return this.querySelector('input[data-doria-column]:checked');
    }

    get selectedBasin() {
      return this.querySelector('[data-doria-basin]')?.selectedOptions[0] || null;
    }

    basinCount(variant) {
      return Number(variant?.basinCount) === 2 || /-2S$/i.test(variant?.sku || '') ? 2 : 1;
    }

    queueGalleryUpdate() {
      if (this.galleryFrame != null || !this.isConnected) return;
      this.galleryFrame = requestAnimationFrame(() => {
        this.galleryFrame = null;
        if (this.controller && !this.controller.signal.aborted) this.updateGallery();
      });
    }

    updateGallery() {
      const productInfo = this.closest('product-info');
      const gallery = productInfo?.querySelector('media-gallery');
      const form = this.form;
      if (!gallery || !form || !this.mediaCounts?.size) return;
      const variant = this.variants.get(String(new FormData(form).get('id')));
      if (!variant) return;
      const count = this.basinCount(variant);
      if (!Array.from(this.mediaCounts.values()).includes(count)) return;
      const mediaId = node => String(node.dataset.mediaId || node.dataset.target || '').split('-').pop();
      const nodes = productInfo.querySelectorAll('media-gallery .product__media-item[data-media-id], media-gallery [data-target], product-modal [data-media-id]');
      let changed = false;
      for (const node of nodes) {
        const mediaCount = this.mediaCounts.get(mediaId(node));
        const hidden = Boolean(mediaCount && mediaCount !== count);
        if (node.hasAttribute('data-doria-media-hidden') !== hidden) {
          node.toggleAttribute('data-doria-media-hidden', hidden);
          changed = true;
        }
      }
      const slides = Array.from(gallery.querySelectorAll('.product__media-item[data-media-id]'));
      const visible = slides.filter(slide => !slide.hasAttribute('data-doria-media-hidden'));
      const active = slides.find(slide => slide.classList.contains('is-active'));
      const next = visible.find(slide => mediaId(slide) === String(variant.mediaId)) ||
        visible.find(slide => this.mediaCounts.get(mediaId(slide)) === count) || visible[0];
      if (next && (!active || active.hasAttribute('data-doria-media-hidden') || this.galleryCount !== count)) {
        if (typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(next.dataset.mediaId, false);
        } else {
          slides.forEach(slide => slide.classList.toggle('is-active', slide === next));
        }
        changed = true;
      }
      this.galleryCount = count;
      if (changed) gallery.querySelectorAll('slider-component').forEach(slider => slider.resetPages?.());
    }

    changed(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (this.contains(target) || target.form === this.form || this.closest('product-info')?.contains(target)) this.update();
    }

    update() {
      const form = this.form;
      if (!form) return;
      this.querySelectorAll('[form]').forEach(control => {
        if (control.getAttribute('form') !== form.id) control.setAttribute('form', form.id);
      });
      const basin = this.selectedBasin;
      if (basin) {
        const image = this.querySelector('[data-doria-basin-image]');
        image.src = basin.dataset.image;
        image.alt = basin.dataset.name;
        this.querySelector('[data-doria-basin-name]').textContent = basin.dataset.name;
        this.querySelector('[data-doria-basin-description]').textContent = basin.dataset.description;
      }
      const column = this.selectedColumn;
      const preview = this.querySelector('[data-doria-column-preview]');
      preview.hidden = !column.value;
      if (column.value) {
        const image = this.querySelector('[data-doria-column-image]');
          image.hidden = !column.dataset.image;
          if (column.dataset.image) image.src = column.dataset.image;
        image.alt = column.dataset.name;
        this.querySelector('[data-doria-column-name]').textContent = column.dataset.name;
      }
      const data = new FormData(form);
      const variant = this.variants.get(String(data.get('id')));
      const countLabel = this.querySelector('[data-doria-basin-count]');
      if (countLabel) countLabel.textContent = this.basinCount(variant) === 2 ? this.texts.twoBasins : this.texts.oneBasin;
      const quantity = Number(data.get('quantity') || 1);
      const total = this.querySelector('[data-doria-total]');
      if (variant && Number.isSafeInteger(quantity) && quantity > 0) {
        total.textContent = this.money.format((variant.price + Number(column.dataset.price || 0)) * quantity / 100);
      } else {
        total.textContent = '—';
      }
      this.queueGalleryUpdate();
    }

    showError(message) {
      const box = this.querySelector('[data-doria-error]');
      this.querySelector('[data-doria-error-message]').textContent = message;
      box.hidden = false;
      box.focus({ preventScroll: true });
    }

    setBusy(busy, form) {
      this.busy = busy;
      form.setAttribute('aria-busy', String(busy));
      if (busy) {
        const productInfo = this.closest('product-info');
        this.lockedControls = [...this.querySelectorAll('select, input[data-doria-column]'), ...productInfo.querySelectorAll('variant-selects input, variant-selects select, input[name="quantity"]')].map(control => ({ control, disabled: control.disabled }));
        this.lockedControls.forEach(({ control }) => { control.disabled = true; });
      } else {
        this.lockedControls?.forEach(({ control, disabled }) => { control.disabled = disabled; });
        this.lockedControls = null;
      }
      const button = form.querySelector('[type="submit"]');
      if (button) {
        if (busy) {
          this.buttonWasDisabled = button.disabled;
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
          button.classList.add('loading');
        } else {
          button.disabled = this.buttonWasDisabled;
          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
        }
        button.querySelector('.loading__spinner')?.classList.toggle('hidden', !busy);
      }
      const status = this.querySelector('[data-doria-status]');
      status.hidden = !busy;
      status.textContent = busy ? this.texts.submitting : '';
    }

    async submit(event) {
      const form = this.form;
      if (!form || event.target !== form || String(this.closest('product-info')?.dataset.productId) !== this.dataset.productId) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.busy) return;
      this.querySelector('[data-doria-error]').hidden = true;
      const data = new FormData(form);
      const variantId = String(data.get('id') || '');
      const variant = this.variants.get(variantId);
      const quantity = Number(data.get('quantity') || 1);
      if (!variant || !variant.available) {
        this.showError(this.texts.invalidVariant);
        return;
      }
      const quantityInput = form.elements.namedItem('quantity');
      if (!Number.isSafeInteger(quantity) || quantity < 1 || (quantityInput && !quantityInput.checkValidity())) {
        this.showError(this.texts.invalidQuantity);
        return;
      }
      const properties = {};
      for (const [key, value] of data.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && typeof value === 'string' && value) properties[match[1]] = value;
      }
      const basin = this.selectedBasin;
      const column = this.selectedColumn;
      if (basin) properties[this.texts.propertyBasin] = basin.dataset.name;
      properties[this.texts.propertyBasinCount] = String(this.basinCount(variant));
      if (column.value) properties[this.texts.propertyColumn] = column.dataset.name;
      const mainItem = { id: variantId, quantity, properties };
      const sellingPlan = data.get('selling_plan');
      if (sellingPlan) mainItem.selling_plan = String(sellingPlan);
      const items = [mainItem];
      if (column.value) items.push({ id: column.value, quantity, properties: { _Complemento: 'DORIA', _DoriaVariant: variantId } });
      this.setBusy(true, form);
      let redirecting = false;
      try {
        const response = await fetch(this.dataset.addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items })
        });
        let result;
        try { result = await response.json(); } catch { result = null; }
        if (!response.ok || result?.status) {
          const detail = result?.description || result?.message;
          this.showError(typeof detail === 'string' ? detail : this.texts.error);
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: 'ob_cart_error', variant_id: variantId, message: typeof detail === 'string' ? detail : this.texts.error });
          return;
        }
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'ob_add_to_cart_success', variant_id: variantId, quantity });
        redirecting = true;
        window.location.assign(this.dataset.cartUrl);
      } catch {
        this.showError(this.texts.unconfirmed);
      } finally {
        if (!redirecting) this.setBusy(false, form);
      }
    }
  });
}
